import React, { useState } from 'react';
import {
  Customer,
  CreateCustomerRequest,
  INDIAN_STATES,
} from '../../types/customer.types';
import {
  Field,
  formatMoney,
  inputClass,
  inputErrorClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { cn } from '../../lib/utils';

interface CustomerFormProps {
  // When set, the form edits this customer; otherwise it creates a new one
  customer?: Customer | null;
  onSubmit: (data: CreateCustomerRequest) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

// The same rules the server applies
const PHONE_PATTERN = /^[+]?[0-9\s\-().]{8,20}$/;
const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_CREDIT_LIMIT = 9999999;

const stateByGstCode = (code: string) => INDIAN_STATES.find((state) => state.gstCode === code);

// A numbered group of related fields
const Section: React.FC<{
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ step, title, description, children }) => (
  <section className="rounded-lg border border-gray-200 bg-white">
    <div className="flex items-center gap-3 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
        {step}
      </span>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-0.5 text-xs text-gray-500">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 p-4 sm:grid-cols-2">{children}</div>
  </section>
);

const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onSubmit, onCancel, isSaving }) => {
  const isEdit = !!customer;

  const [form, setForm] = useState({
    name: customer?.name || '',
    code: customer?.code || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    status: customer && !customer.isActive ? 'inactive' : 'active',
    address: customer?.address || '',
    city: customer?.city || '',
    state: customer?.state || '',
    pincode: customer?.pincode || '',
    contactPerson: customer?.contactPerson || '',
    contactPhone: customer?.contactPhone || '',
    gstNumber: customer?.gstNumber || '',
    panNumber: customer?.panNumber || '',
    creditLimit: customer ? String(customer.creditLimit ?? 0) : '',
    paymentTerms: String(customer?.paymentTerms ?? 30),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setField = (name: keyof typeof form, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'gstNumber') {
        next.gstNumber = value.toUpperCase().replace(/\s/g, '');
        // A complete GSTIN carries the state and the PAN, so fill in what is still empty
        if (GST_PATTERN.test(next.gstNumber)) {
          if (!prev.panNumber.trim()) next.panNumber = next.gstNumber.slice(2, 12);
          if (!prev.state) next.state = stateByGstCode(next.gstNumber.slice(0, 2))?.name || '';
        }
      }
      if (name === 'panNumber') next.panNumber = value.toUpperCase().replace(/\s/g, '');
      if (name === 'pincode') next.pincode = value.replace(/\D/g, '').slice(0, 6);
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    const text = (key: keyof typeof form) => form[key].trim();

    if (!text('name')) next.name = 'Customer name is required';
    else if (text('name').length < 2 || text('name').length > 100) next.name = 'Name must be 2 to 100 characters';

    if (!text('code')) next.code = 'Customer code is required';
    else if (text('code').length < 3 || text('code').length > 20) next.code = 'Code must be 3 to 20 characters';

    if (!text('email')) next.email = 'Email is required';
    else if (!EMAIL_PATTERN.test(text('email'))) next.email = 'Enter a valid email address';

    if (!text('phone')) next.phone = 'Phone is required';
    else if (!PHONE_PATTERN.test(text('phone'))) next.phone = 'Enter 8 to 20 digits; +, spaces, hyphens and brackets are allowed';

    if (!text('address')) next.address = 'Address is required';
    else if (text('address').length < 5 || text('address').length > 200) next.address = 'Address must be 5 to 200 characters';

    if (!text('city')) next.city = 'City is required';
    else if (text('city').length < 2 || text('city').length > 50) next.city = 'City must be 2 to 50 characters';

    if (!form.state) next.state = 'State is required';

    if (!form.pincode) next.pincode = 'Pincode is required';
    else if (!/^[0-9]{6}$/.test(form.pincode)) next.pincode = 'Pincode must be 6 digits';

    if (!text('contactPerson')) next.contactPerson = 'Contact person is required';
    else if (text('contactPerson').length < 2 || text('contactPerson').length > 50) {
      next.contactPerson = 'Contact person must be 2 to 50 characters';
    }

    if (!text('contactPhone')) next.contactPhone = 'Contact phone is required';
    else if (!PHONE_PATTERN.test(text('contactPhone'))) {
      next.contactPhone = 'Enter 8 to 20 digits; +, spaces, hyphens and brackets are allowed';
    }

    // Tax numbers are optional (an unregistered customer has no GSTIN) but must be well formed
    if (form.gstNumber && !GST_PATTERN.test(form.gstNumber)) {
      next.gstNumber = 'A GST number has 15 characters, like 27ABCDE1234F1Z5';
    } else if (form.gstNumber) {
      const gstState = stateByGstCode(form.gstNumber.slice(0, 2));
      const chosenState = INDIAN_STATES.find((state) => state.name === form.state);
      if (!gstState) {
        next.gstNumber = 'The first two digits are not a valid GST state code';
      } else if (chosenState && chosenState.gstCode !== gstState.gstCode) {
        next.gstNumber = `This GST number belongs to ${gstState.name}, but the state is ${chosenState.name}`;
      }
    }

    if (form.panNumber && !PAN_PATTERN.test(form.panNumber)) {
      next.panNumber = 'A PAN has 10 characters, like ABCDE1234F';
    } else if (form.panNumber && GST_PATTERN.test(form.gstNumber) && form.gstNumber.slice(2, 12) !== form.panNumber) {
      next.panNumber = 'The PAN does not match the PAN inside the GST number';
    }

    const creditLimit = Number(form.creditLimit || 0);
    if (!Number.isFinite(creditLimit) || creditLimit < 0) next.creditLimit = 'Credit limit must be 0 or more';
    else if (creditLimit > MAX_CREDIT_LIMIT) next.creditLimit = `Credit limit can be at most ${formatMoney(MAX_CREDIT_LIMIT)}`;

    const paymentTerms = Number(form.paymentTerms);
    if (!Number.isInteger(paymentTerms) || paymentTerms < 1 || paymentTerms > 365) {
      next.paymentTerms = 'Enter a whole number of days from 1 to 365';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateCustomerRequest = {
      name: form.name.trim(),
      code: form.code.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      // Null clears a tax number that was removed in the form
      gstNumber: form.gstNumber || null,
      panNumber: form.panNumber || null,
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state,
      pincode: form.pincode,
      contactPerson: form.contactPerson.trim(),
      contactPhone: form.contactPhone.trim(),
      creditLimit: Number(form.creditLimit || 0),
      paymentTerms: Number(form.paymentTerms),
      isActive: form.status === 'active',
    };

    try {
      await onSubmit(payload);
    } catch (error) {
      // The caller reports the failure; keep the form open so nothing is lost
    }
  };

  const fieldClass = (key: string) => cn(inputClass, errors[key] && inputErrorClass);
  const errorCount = Object.values(errors).filter(Boolean).length;
  const creditLimit = Number(form.creditLimit || 0);
  const location = [form.city.trim(), form.state].filter(Boolean).join(', ');
  // A state saved before the list existed stays selectable
  const hasUnlistedState = !!form.state && !INDIAN_STATES.some((state) => state.name === form.state);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <Section step={1} title="Company" description="Who the customer is and how to reach the business.">
            <Field label="Customer Name" htmlFor="cust-name" required error={errors.name}>
              <input
                id="cust-name"
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                className={fieldClass('name')}
                placeholder="e.g. Acme Engineering Pvt Ltd"
              />
            </Field>

            <Field
              label="Customer Code"
              htmlFor="cust-code"
              required
              error={errors.code}
              hint="A short unique code used on orders and invoices."
            >
              <input
                id="cust-code"
                type="text"
                value={form.code}
                onChange={(e) => setField('code', e.target.value)}
                className={cn(fieldClass('code'), 'font-mono')}
                placeholder="e.g. ACME001"
              />
            </Field>

            <Field label="Email" htmlFor="cust-email" required error={errors.email}>
              <input
                id="cust-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                className={fieldClass('email')}
                placeholder="purchase@company.in"
              />
            </Field>

            <Field label="Phone" htmlFor="cust-phone" required error={errors.phone}>
              <input
                id="cust-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                className={fieldClass('phone')}
                placeholder="+91-9876543210"
              />
            </Field>

            <Field
              label="Status"
              htmlFor="cust-status"
              hint="An inactive customer is kept for records but cannot be put on new orders."
            >
              <select
                id="cust-status"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
                className={inputClass}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </Section>

          <Section step={2} title="Billing address" description="Printed on quotes and invoices, and used as the delivery address by default.">
            <Field label="Address" htmlFor="cust-address" required error={errors.address} className="sm:col-span-2">
              <textarea
                id="cust-address"
                rows={2}
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                className={fieldClass('address')}
                placeholder="Building, street, area"
              />
            </Field>

            <Field label="City" htmlFor="cust-city" required error={errors.city}>
              <input
                id="cust-city"
                type="text"
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                className={fieldClass('city')}
              />
            </Field>

            <Field label="State" htmlFor="cust-state" required error={errors.state}>
              <select
                id="cust-state"
                value={form.state}
                onChange={(e) => setField('state', e.target.value)}
                className={fieldClass('state')}
              >
                <option value="">Select a state</option>
                {hasUnlistedState && <option value={form.state}>{form.state}</option>}
                {INDIAN_STATES.map((state) => (
                  <option key={state.gstCode} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Pincode" htmlFor="cust-pincode" required error={errors.pincode}>
              <input
                id="cust-pincode"
                type="text"
                inputMode="numeric"
                value={form.pincode}
                onChange={(e) => setField('pincode', e.target.value)}
                className={fieldClass('pincode')}
                placeholder="6 digits"
              />
            </Field>
          </Section>

          <Section step={3} title="Contact person" description="The person your team deals with at this customer.">
            <Field label="Contact Person" htmlFor="cust-contact" required error={errors.contactPerson}>
              <input
                id="cust-contact"
                type="text"
                value={form.contactPerson}
                onChange={(e) => setField('contactPerson', e.target.value)}
                className={fieldClass('contactPerson')}
              />
            </Field>

            <Field label="Contact Phone" htmlFor="cust-contact-phone" required error={errors.contactPhone}>
              <input
                id="cust-contact-phone"
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setField('contactPhone', e.target.value)}
                className={fieldClass('contactPhone')}
                placeholder="+91-9876543210"
              />
            </Field>
          </Section>

          <Section step={4} title="Tax and credit" description="GST registration, and how much the customer may owe.">
            <Field
              label="GST Number"
              htmlFor="cust-gst"
              error={errors.gstNumber}
              hint="Leave empty for an unregistered customer."
            >
              <input
                id="cust-gst"
                type="text"
                maxLength={15}
                value={form.gstNumber}
                onChange={(e) => setField('gstNumber', e.target.value)}
                className={cn(fieldClass('gstNumber'), 'font-mono')}
                placeholder="27ABCDE1234F1Z5"
              />
            </Field>

            <Field
              label="PAN Number"
              htmlFor="cust-pan"
              error={errors.panNumber}
              hint="Filled in from the GST number when left empty."
            >
              <input
                id="cust-pan"
                type="text"
                maxLength={10}
                value={form.panNumber}
                onChange={(e) => setField('panNumber', e.target.value)}
                className={cn(fieldClass('panNumber'), 'font-mono')}
                placeholder="ABCDE1234F"
              />
            </Field>

            <Field
              label="Credit Limit (₹)"
              htmlFor="cust-credit"
              error={errors.creditLimit}
              hint="The most the customer may owe on open orders. 0 means no limit."
            >
              <input
                id="cust-credit"
                type="number"
                min="0"
                step="1"
                value={form.creditLimit}
                onChange={(e) => setField('creditLimit', e.target.value)}
                className={fieldClass('creditLimit')}
                placeholder="0"
              />
            </Field>

            <Field
              label="Payment Terms (Days)"
              htmlFor="cust-terms"
              error={errors.paymentTerms}
              hint="Days allowed for payment after an invoice."
            >
              <input
                id="cust-terms"
                type="number"
                min="1"
                max="365"
                step="1"
                value={form.paymentTerms}
                onChange={(e) => setField('paymentTerms', e.target.value)}
                className={fieldClass('paymentTerms')}
              />
            </Field>
          </Section>
        </div>

        {/* Right: the customer so far, and how credit is applied */}
        <aside className="xl:col-span-1">
          <div className="space-y-5 xl:sticky xl:top-4">
            <section className="rounded-lg border border-gray-200 bg-white">
              <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Summary
              </h4>
              <div className="space-y-4 p-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Customer</p>
                  <p className="font-medium text-gray-900">{form.name.trim() || 'Not named yet'}</p>
                  {form.code.trim() && <p className="font-mono text-xs text-gray-500">{form.code.trim()}</p>}
                </div>

                <div className="rounded-md bg-indigo-50 px-3 py-3">
                  <p className="text-xs text-indigo-700">Credit limit</p>
                  <p className="text-2xl font-semibold text-indigo-900 tabular-nums">
                    {creditLimit > 0 ? formatMoney(creditLimit) : 'No limit'}
                  </p>
                  <p className="mt-0.5 text-xs text-indigo-700">
                    Payment within {Number(form.paymentTerms) || 0} days of invoice
                  </p>
                </div>

                <dl className="space-y-2">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Location</dt>
                    <dd className="text-right font-medium text-gray-900">{location || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">GST</dt>
                    <dd className="text-right font-medium text-gray-900">
                      {form.gstNumber ? 'Registered' : 'Unregistered'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Contact</dt>
                    <dd className="text-right font-medium text-gray-900">{form.contactPerson.trim() || '—'}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-600">Status</dt>
                    <dd className="font-medium text-gray-900">{form.status === 'active' ? 'Active' : 'Inactive'}</dd>
                  </div>
                </dl>
              </div>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
              <h4 className="text-sm font-semibold text-gray-900">How credit is checked</h4>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Outstanding is the value of the customer's open sales orders.</li>
                <li>A new order is checked against the limit before it is saved.</li>
                <li>A limit of 0 turns the check off for this customer.</li>
              </ul>
            </section>
          </div>
        </aside>
      </div>

      {/* Footer keeps the problems count in view next to the buttons */}
      <div className="-mx-6 -mb-4 mt-6 flex flex-col gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm">
          {errorCount > 0 ? (
            <span className="font-medium text-red-600">
              {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} attention
            </span>
          ) : (
            <span className="text-gray-600">Fields marked * are required</span>
          )}
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button type="button" onClick={onCancel} disabled={isSaving} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? (isEdit ? 'Updating...' : 'Creating...') : isEdit ? 'Update Customer' : 'Create Customer'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CustomerForm;
